// Every empty board explains itself (Features 4, part 3): the same calm card, centred on each kind of board while
// it has nothing on it. What the board is for in a line, a small picture of it filled in (drawn in the board's own
// theme), two or three asks that put their words in the chat's box, ready to send, and the one way to start by
// hand. It goes as soon as the board has something on it.

import { useState, useSyncExternalStore, type ReactNode } from "react";
import { ArrowUp, ArrowUpRight, Sparkles, X } from "lucide-react";
import type { BoardKind } from "../../shared/types";
import { openChat } from "../agent/store";
import { askLabel, handOff, useBuilder } from "../agent/handoff";
import { cx } from "../lib/util";
import { useStore } from "../state/store";
import { KindPicture } from "./pictures";
import { KIND_START } from "./prompts";
import "./onboarding.css";

export interface StartByHand {
  label: string;
  icon?: ReactNode;
  run: () => void;
  /** the key that does the same, said beside it */
  kbd?: string;
}

/** Put an ask in the chat's box, ready to send. */
export const askInChat = (words: string) => openChat(words, { send: false });

// a card put away on a board, for this visit: over a surface you work on (a sheet's cells, a slide) it can step aside
const away = new Set<string>();
const subs = new Set<() => void>();
const putAway = (board: string) => {
  away.add(board);
  for (const fn of subs) fn();
};
/** Whether the card on this board was put away in this visit. */
export function useAway(board: string | null | undefined): boolean {
  return useSyncExternalStore(
    (fn) => (subs.add(fn), () => void subs.delete(fn)),
    () => !!board && away.has(board),
  );
}

/** A box on the card to tell the agent what to build, in words of the user's own (David, 1 Nov: a Custom board's card
 * offered three asks and nowhere to type one). Its button says who answers, as the chat's button top right does: with
 * words it sends them, as handOff gives any request; empty, it opens the chat to type in. */
export interface PromptBox {
  placeholder: string;
  /** the same request worded for an agent outside the board, which is copied for it */
  forAgent: (words: string) => string;
}

/** An agent at work on the board, said in place of the asks (David, 1 Nov: a Custom board being built showed nothing). */
export interface AtWork {
  who: string;
  /** its last change here, in its own words */
  last: string | null;
}

function Prompt({ box }: { box: PromptBox }) {
  const [text, setText] = useState("");
  const label = askLabel(useBuilder());
  const go = () => {
    const words = text.trim();
    if (!words) return openChat();
    handOff({ chat: words, agent: box.forAgent(words) });
    setText("");
  };
  return (
    <form className="ob-prompt" onSubmit={(e) => (e.preventDefault(), go())}>
      <textarea
        value={text}
        rows={2}
        maxLength={4000}
        placeholder={box.placeholder}
        aria-label="What your agent should build"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends, as in the chat; Shift and Enter is a new line
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            go();
          }
        }}
      />
      <button type="submit" className="btn btn-sm btn-primary ob-prompt-go" data-tip={text.trim() ? "Send it to your agent" : "Open the chat"}>
        {label}
        <ArrowUp size={14} />
      </button>
    </form>
  );
}

/** board: where the card sits over a surface the user works on, so its X puts it away there for this visit. */
/** wide: the picture beside the words, for a page's column (a doc's first page). */
/** prompt: a box to type the request in; working: an agent at work here, said in place of what to ask. */
export function EmptyKind({ kind, byHand, note, asks, className, compact, wide, board, prompt, working }: { kind: BoardKind; byHand?: StartByHand | null; note?: ReactNode; asks?: string[] | null; className?: string; compact?: boolean; wide?: boolean; board?: string; prompt?: PromptBox | null; working?: AtWork | null }) {
  const start = KIND_START[kind];
  // a coworker on a board shared with them has no chat, so no ask is offered there: it would do nothing
  const guest = useStore((s) => !!s.guest);
  const list = asks === null || guest ? [] : (asks ?? start.empty);
  return (
    <div className={cx("ob-empty", compact && "is-compact", wide && "is-wide", working && "is-working", className)} data-ob="empty" data-kind={kind} onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      {board && (
        <button className="icon-btn sm ob-empty-x" aria-label="Put this away" data-tip="Put this away" onClick={() => putAway(board)}>
          <X size={13} />
        </button>
      )}
      <div className="ob-empty-pic">
        <KindPicture kind={kind} />
      </div>
      <div className="ob-empty-words">
      {working ? (
        <div className="ob-working" role="status" aria-live="polite">
          <p className="ob-empty-line">
            <span className="ob-spin" aria-hidden />
            {working.who} is working on this board
          </p>
          {working.last && <p className="ob-empty-note ob-working-last">{working.last}</p>}
          <p className="ob-empty-note">It shows here as soon as it's ready.</p>
        </div>
      ) : (
      <>
      <p className="ob-empty-line">{start.line}</p>
      {note}
      {prompt && !guest && <Prompt box={prompt} />}
      {list.length > 0 && (
        <>
          <div className="ob-empty-sec">{prompt && !guest ? "Or start from one of these" : "Ask your agent"}</div>
          <div className="ob-empty-asks">
            {list.map((a) => (
              <button key={a} className="ob-ask" data-ask={a} onClick={() => askInChat(a)} data-tip="Put it in the chat, ready to send">
                <Sparkles size={13} className="ob-ask-ico" />
                <span>{a}</span>
                <ArrowUpRight size={13} className="ob-ask-go" />
              </button>
            ))}
          </div>
        </>
      )}
      {byHand && (
        <div className="ob-empty-hand">
          <button className="btn btn-sm" onClick={byHand.run}>
            {byHand.icon}
            {byHand.label}
          </button>
          {byHand.kbd && (
            <span className="ob-empty-kbd">
              or press <kbd>{byHand.kbd}</kbd>
            </span>
          )}
        </div>
      )}
      </>
      )}
      </div>
    </div>
  );
}
